import { useSyncExternalStore } from "react";

/**
 * Tiny history-based router — no dependency, for this SPA's handful of flat top-level routes. Gives each
 * view a real URL, so back/forward, refresh (nginx serves index.html for any non-/api path), deep-links
 * and bookmarks all work — without pulling in react-router for what a ~30-line hook covers. If routing
 * ever grows nested/param-heavy, swapping this for react-router is mechanical (same useLocation/navigate).
 */

function subscribe(cb: () => void): () => void {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
}

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Current location (pathname + search), re-rendering the caller on navigate / back / forward.
 * `useSyncExternalStore` is the React-18-correct subscription (tear-free, StrictMode-safe).
 */
export function useLocation(): { pathname: string; search: string } {
  const href = useSyncExternalStore(subscribe, snapshot, snapshot);
  const q = href.indexOf("?");
  return q === -1 ? { pathname: href, search: "" } : { pathname: href.slice(0, q), search: href.slice(q) };
}

/**
 * Navigate to `to`. `history.pushState` on its own does NOT emit a `popstate`, so we dispatch one to
 * wake `useLocation` subscribers. A no-op when already there (unless `replace`), so a redundant nav
 * doesn't spam history.
 */
export function navigate(to: string, opts?: { replace?: boolean }): void {
  if (window.location.pathname + window.location.search === to && !opts?.replace) return;
  if (opts?.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
